import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import type { CardVerificationCodeElement } from '@basis-theory/basis-theory-js/types/elements';
import { resolveBasisTheoryClient, type BasisTheoryClient } from 'services/basisTheory';
import errorMonitoring from '@/errorMonitoring';

type CvvElementStatus = 'idle' | 'loading' | 'ready' | 'error';

interface UseBasisTheoryCvvElementOptions {
  active: boolean;
  placeholder: string;
  publicKey: string;
}

interface UseBasisTheoryCvvElementResult {
  clientRef: MutableRefObject<BasisTheoryClient | null>;
  elementRef: MutableRefObject<CardVerificationCodeElement | null>;
  isComplete: boolean;
  status: CvvElementStatus;
  targetId: string;
}

const TARGET_ID = 'bt-stored-card-cvv';
const ELEMENT_STYLE = {
  base: {
    'fontFamily': 'inherit',
    'fontSize': '16px',
    'lineHeight': '48px',
    'color': '#32325d',
    '::placeholder': { color: '#aab7c4' },
  },
};

/** Mount and own the secure CVV element used by the selected stored card. */
function useBasisTheoryCvvElement({
  active,
  placeholder,
  publicKey,
}: UseBasisTheoryCvvElementOptions): UseBasisTheoryCvvElementResult {
  const clientRef = useRef<BasisTheoryClient | null>(null);
  const elementRef = useRef<CardVerificationCodeElement | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [status, setStatus] = useState<CvvElementStatus>('idle');

  useEffect(() => {
    if (!active) {
      clientRef.current = null;
      elementRef.current = null;
      setIsComplete(false);
      setStatus('idle');
      return undefined;
    }

    let cancelled = false;
    let ownedElement: CardVerificationCodeElement | null = null;
    setIsComplete(false);
    setStatus('loading');

    resolveBasisTheoryClient(publicKey)
      .then(async (client) => {
        if (cancelled) return;
        const target = document.getElementById(TARGET_ID);
        if (!target) throw new Error('stored_card_cvv_target_missing');

        ownedElement = client.createElement('cardVerificationCode', {
          targetId: TARGET_ID,
          inputMode: 'numeric',
          autoComplete: 'cc-csc',
          validateOnChange: true,
          placeholder,
          style: ELEMENT_STYLE,
        });
        ownedElement.on('change', (event) => {
          if (!cancelled) setIsComplete(Boolean(event.complete && !event.empty && event.valid));
        });
        await ownedElement.mount(target);
        if (cancelled) {
          ownedElement.unmount();
          return;
        }

        clientRef.current = client;
        elementRef.current = ownedElement;
        setStatus('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        errorMonitoring.notify({ error, info: { stage: 'stored_card_cvv_mount' } });
        setStatus('error');
      });

    return () => {
      cancelled = true;
      clientRef.current = null;
      elementRef.current = null;
      if (ownedElement) {
        try {
          ownedElement.unmount();
        } catch (error) {
          errorMonitoring.notify({ error, info: { stage: 'stored_card_cvv_unmount' } });
        }
      }
    };
  }, [active, placeholder, publicKey]);

  return { clientRef, elementRef, isComplete, status, targetId: TARGET_ID };
}

export type { CvvElementStatus };
export { useBasisTheoryCvvElement };
